import { useContext, useState } from 'react'
import { Formik, Form } from 'formik' 
import * as Yup from 'yup';
import Button from './Button'
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from "../context/UserContext";
import { toast } from 'react-toastify';
import { formatError, logError } from '../services/logging';
import EmailInput from './Forms/EmailInput';
import PasswordInput from './Forms/PasswordInput';
import PassConfirmInput from './Forms/PassConfirmInput';
import { API_URL } from '../config/api';
import BetaKeyInput from './Forms/BetaKeyInput';

// prop "type" expects either "signUp" or "logIn"

const AuthForm = ({ type }) => {
    const navigate = useNavigate()
    const { logInUser } = useContext(UserContext)
    const isSignUp = type === "signUp"
    const [showResetPassword, setShowResetPassword] = useState(false)

    return (
        <Formik
            initialValues={{
                email: '',
                password: '',
                ...(isSignUp && {
                    passwordConfirm: '',
                    betaKey: ''
                })
            }}
            validationSchema={Yup.object({
                email: Yup.string()
                    .email("Invalid email address")
                    .required("Required"),
                ...(isSignUp && {
                    password: Yup.string()
                        .min(8, "Must be at least 8 characters")
                        .required("Required"),
                    passwordConfirm: Yup.string()
                        .oneOf([Yup.ref('password')], 'Passwords must match')
                        .required('Required'),
                    betaKey: Yup.string()
                        .required('Required')
                    })
            })}
            onSubmit={async (values, { setSubmitting }) => {
                try {
                    const resp = await fetch(`${API_URL}/api/${isSignUp ? "register" : "login"}`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        credentials: "include",
                        body: JSON.stringify(values)
                    })

                    const data = await resp.json()

                    if (resp.ok) {
                        localStorage.removeItem("failedLoginAttempts")
                        logInUser(data)

                        if (isSignUp) {
                            toast.success("Account created, welcome!")
                        }

                        navigate('/questionnaire')
                    } else {

                        if (resp.status === 401) {
                            const currentAttempts = parseInt(localStorage.getItem("failedLoginAttempts")) || 0
                            const newAttempts = currentAttempts + 1
                            localStorage.setItem("failedLoginAttempts", newAttempts)

                            if (newAttempts >= 3) {
                                toast.info("Having trouble logging in? You can reset your password.")
                                setShowResetPassword(true)
                            }
                        }
                        logError(data)
                        toast.error(data.message || "Login failed")
                    }
                } catch (error) {
                    logError(formatError(error))
                    toast.error("An unexpected error occurred. Please try again.")
                } finally {
                    setSubmitting(false)
                }
            }}
        >
            {({ errors, touched, dirty, isValid, isSubmitting }) => (
                <Form>
                    <EmailInput errors={errors} touched={touched} />

                    <PasswordInput errors={errors} touched={touched} isSignUp={isSignUp} />
                    {showResetPassword && (
                        <div className="reset-link">
                            <Link to='/reset-password'>Reset password</Link>
                        </div>
                    )}

                    {isSignUp && (
                        <>
                            <PassConfirmInput errors={errors} touched={touched} />
                            <BetaKeyInput errors={errors} touched={touched} />
                        </>
                    )}

                    <Button 
                        variant="primary" 
                        text={isSignUp ? "Sign up" : "Log in"} 
                        type="submit" 
                        disabled={!dirty || !isValid || isSubmitting}
                    />
                </Form>
            )}
        </Formik>
    )
}

export default AuthForm